import React, { useCallback, useRef, useEffect } from 'react';
import { IconVolume, IconVolumeOff } from '@tabler/icons-react';

interface VolumeControlProps {
  volume: number;
  isMuted: boolean;
  onVolumeChange: (volume: number) => void;
  onToggleMute: () => void;
  className?: string;
}

const VolumeControl: React.FC<VolumeControlProps> = ({
  volume,
  isMuted,
  onVolumeChange,
  onToggleMute,
  className = ''
}) => {
  const [isVolumeHovered, setIsVolumeHovered] = React.useState(false);
  const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
    };
  }, []);

  const handleVolumeSliderClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const percentage = 1 - clickY / rect.height; // Invert because we want top = 100%, bottom = 0%
      const newVolume = Math.max(0, Math.min(1, percentage));
      const roundedVolume = Math.round(newVolume * 100) / 100; // Round to 2 decimal places

      onVolumeChange(roundedVolume);
    },
    [onVolumeChange]
  );

  const handleVolumeMouseEnter = useCallback(() => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
      volumeTimeoutRef.current = null;
    }
    setIsVolumeHovered(true);
  }, []);

  const handleVolumeMouseLeave = useCallback(() => {
    volumeTimeoutRef.current = setTimeout(() => {
      setIsVolumeHovered(false);
    }, 200);
  }, []);

  return (
    <div className={`relative flex items-center gap-4 ${className}`}>
      <div className="group relative" onMouseEnter={handleVolumeMouseEnter} onMouseLeave={handleVolumeMouseLeave}>
        <button
          onClick={onToggleMute}
          className="flex cursor-pointer items-center justify-center text-white/75 hover:text-white"
          data-testid="mute-button"
        >
          {isMuted ? <IconVolumeOff size={16} /> : <IconVolume size={16} />}
        </button>

        {/* Volume Slider - appears on hover */}
        {isVolumeHovered && (
          <div
            className="absolute bottom-12 left-1/2 z-20 -translate-x-1/2"
            onMouseEnter={handleVolumeMouseEnter}
            onMouseLeave={handleVolumeMouseLeave}
            data-testid="volume-slider-container"
          >
            <div className="relative">
              {/* Volume Slider Container */}
              <div
                className="h-[110px] w-10 cursor-pointer rounded-lg bg-slate-950 px-4 py-2"
                onClick={handleVolumeSliderClick}
                data-testid="volume-slider"
              >
                {/* Volume Track */}
                <div className="relative mx-auto my-1 h-full w-1 rounded-full bg-gray-600">
                  {/* Volume Fill */}
                  <div
                    className="absolute bottom-0 w-full rounded-full bg-white transition-all duration-150"
                    style={{ height: `${Math.max(0, Math.min(100, volume * 100))}%` }}
                    data-testid="volume-fill"
                  />
                  {/* Volume Knob */}
                  <div
                    className="absolute size-3 -translate-x-1 rounded-full bg-white transition-all duration-150"
                    style={{ bottom: `${Math.max(0, Math.min(100, volume * 100))}%`, marginBottom: '-6px' }}
                    data-testid="volume-knob"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VolumeControl;
